import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { listDocumentTypes, searchWorkspaceDocuments } from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

const DOC_TYPES_FALLBACK = [
  "Commercial Invoice",
  "Proforma Invoice",
  "Packing List",
  "Purchase Order",
  "Inspection Report",
  "Certificate of Origin",
  "Health Certificate",
  "Analysis Certificate",
  "Bill of Lading",
  "Booking Confirmation",
  "Freight Invoice",
  "Insurance Certificate",
  "Export Declaration",
  "Customs Documents",
  "Other",
];

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState(DOC_TYPES_FALLBACK);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [q, setQ] = useState(params.get("q") || "");
  const [documentType, setDocumentType] = useState(params.get("document_type") || "");
  const [poNumber, setPoNumber] = useState(params.get("po_number") || "");
  const [status, setStatus] = useState(params.get("status") || "Active");
  const [containerNumber, setContainerNumber] = useState(params.get("container_number") || "");
  const [shipment, setShipment] = useState(params.get("shipment") || "");

  const load = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await searchWorkspaceDocuments({
        q: (filters.q ?? q) || undefined,
        document_type: (filters.document_type ?? documentType) || undefined,
        po_number: (filters.po_number ?? poNumber) || undefined,
        container_number: (filters.container_number ?? containerNumber) || undefined,
        shipment: (filters.shipment ?? shipment) || undefined,
        status: (filters.status ?? status) || undefined,
        po_id: params.get("po_id") || undefined,
        rfq_id: params.get("rfq_id") || undefined,
        buyer_id: params.get("buyer_id") || undefined,
      });
      setRows(res.documents || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listDocumentTypes()
      .then((r) => setTypes(r.types || DOC_TYPES_FALLBACK))
      .catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySearch = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (documentType) next.set("document_type", documentType);
    if (poNumber) next.set("po_number", poNumber);
    if (status) next.set("status", status);
    if (containerNumber) next.set("container_number", containerNumber);
    if (shipment) next.set("shipment", shipment);
    setParams(next);
    load({
      q,
      document_type: documentType,
      po_number: poNumber,
      status,
      container_number: containerNumber,
      shipment,
    });
  };

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.documentsList}>
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              Documents Hub
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/workspace/shipments"
              className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 hover:text-neutral-800"
            >
              Shipments
            </Link>
            <Link
              to="/workspace/purchase-orders"
              className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 hover:text-neutral-800"
            >
              Purchase Orders
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
          Workspace
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-light tracking-tight">Documents Hub™️</h1>
        <p className="mt-3 text-sm text-neutral-500 max-w-2xl">
          Central import documentation — commercial, inspection and shipping files linked to your
          purchase orders and shipments.
        </p>

        {/* Filters */}
        <div
          data-testid={WORKSPACE.documentsFilters}
          className="mt-8 border border-neutral-200 bg-white rounded-sm p-4 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
        >
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Keyword search…"
            className="h-10 rounded-sm"
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="h-10 rounded-sm border border-neutral-200 px-3 text-sm bg-white"
          >
            <option value="">All document types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <Input
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="PO Number"
            className="h-10 rounded-sm"
          />
          <Input
            value={shipment}
            onChange={(e) => setShipment(e.target.value)}
            placeholder="Shipment"
            className="h-10 rounded-sm"
          />
          <Input
            value={containerNumber}
            onChange={(e) => setContainerNumber(e.target.value)}
            placeholder="Container Number"
            className="h-10 rounded-sm"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-sm border border-neutral-200 px-3 text-sm bg-white"
          >
            <option value="Active">Active</option>
            <option value="Archived">Archived</option>
            <option value="">All statuses</option>
          </select>
          <Button
            onClick={applySearch}
            className="h-10 rounded-sm bg-[hsl(217,90%,25%)] text-white"
          >
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
        </div>

        <div className="mt-6 border border-neutral-200 bg-white rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-16 grid place-items-center">
              <Loader2 className="w-5 h-5 animate-spin text-[hsl(217,90%,25%)]" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-sm text-neutral-500">
              No documents found. Files uploaded to inspections and ops uploads appear here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[hsl(60,5%,96%)]">
                  {[
                    "Document Name",
                    "Document Type",
                    "PO Number",
                    "Shipment",
                    "Status",
                    "Uploaded By",
                    "Upload Date",
                    "Version",
                  ].map((h) => (
                    <TableHead
                      key={h}
                      className="uppercase text-[10px] tracking-[0.2em] font-semibold text-neutral-500"
                    >
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((d) => (
                  <TableRow
                    key={d.id}
                    data-testid={WORKSPACE.documentsRow}
                    className="cursor-pointer hover:bg-[hsl(60,5%,97%)]"
                    onClick={() => navigate(`/workspace/documents/${d.id}`)}
                  >
                    <TableCell className="text-sm font-medium">{d.name}</TableCell>
                    <TableCell className="text-sm">{d.document_type}</TableCell>
                    <TableCell className="font-mono text-xs">{d.po_number || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {d.tracking_number || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`rounded-sm uppercase text-[10px] tracking-wider text-white ${
                          d.status === "Archived" ? "bg-neutral-500" : "bg-[hsl(217,90%,25%)]"
                        }`}
                      >
                        {d.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-600">{d.uploaded_by || "—"}</TableCell>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-sm font-mono">v{d.version}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
